define('presentation/signup/viewModels/signupPhoneNumberLocalViewModel',[
    'common/converters/phoneNumberFormatter',
    'common/promises/promiseFactory',
    'common/storage/workSessionIdStorage',
    'constants/numberPickerConstants',
    'constants/signupSessionFlowConstants',
    'constants/signupSessionStatusConstants',
    'presentation/signupNumberPicker/presentationObjects/signupNumberPickerPresentationObject',
    'presentation/signup/common/signupNavigationManager',
    'presentation/signup/facades/signupPhoneNumberLocalFacade',
    'settings/navigationConfiguration',
], function() {

    return function() {
        const self = this;

        const PromiseFactoryConstructor = require('common/promises/promiseFactory');
        const _promiseFactory = new PromiseFactoryConstructor();

        const PhoneNumberFormatterConstructor = require('common/converters/phoneNumberFormatter');
        const _phoneNumberFormatter = new PhoneNumberFormatterConstructor();

        const NumberPresentationObjectConstructor = require('presentation/signupNumberPicker/presentationObjects/signupNumberPickerPresentationObject');

        const _i18n = require('i18next');

        const _navigationConfiguration = require('settings/navigationConfiguration');
        const _signupSessionFlowConstants = require('constants/signupSessionFlowConstants');
        const _numberPickerConstants = require('constants/numberPickerConstants');
        const _signupSessionStatusConstants = require('constants/signupSessionStatusConstants');
        const _workSessionIdStorage = require('common/storage/workSessionIdStorage');

        let _facade = null;
        let _signupNavigationManager = null;
        let _disposables = [];

        const _isSignupForCustomer = _workSessionIdStorage.getWorkSessionId() !== null && _workSessionIdStorage.getWorkSessionId() !== undefined;

        self.headingText = _i18n.t('signupPhoneNumberLocal:headingText');
        self.subtitleText = _i18n.t('signupPhoneNumberLocal:subtitleText');

        self.isSignupForCustomer = ko.observable(_isSignupForCustomer);
        self.isCompositionComplete = ko.observable(false);
        self.isModalActive = ko.observable(false);
        self.isModalVisible = ko.observable(false);
        self.isXCloseVisible = true;
        self.modalPhoneNumber = ko.observable("");
        self.isScrollActive = ko.observable(false);

        self.numberType = _numberPickerConstants.numberTypes.local;
        self.selectedPhoneNumbers = ko.observableArray([]);

        self.signupSessionHasNumber = ko.pureComputed(() => {
            return self.selectedPhoneNumbers().length > 0;
        });

        self.flow = ko.observable();
        self.isFlowBTextVisible = ko.computed(() => self.flow() === _signupSessionFlowConstants.flowB);

        self.searchType = ko.observable(_numberPickerConstants.searchTypeConstants.default);
        self.vanityString = ko.observable('');
        self.geoLocation = null;

        self.showValidationMessage = ko.observable(false);
        const _resetShowValidationMessage = (selectedPhoneNumbers => {
            if (self.showValidationMessage()) {
                self.showValidationMessage(selectedPhoneNumbers.length < 1);
            }
        });
        _disposables.push(self.selectedPhoneNumbers.subscribe(_resetShowValidationMessage));

        self.navigateToFlowD = () => {
            _signupNavigationManager.navigateToRoute(_navigationConfiguration.routesById.signupPhoneNumberType.routeId, _signupSessionFlowConstants.flowD);
        };

        self.onBackArrowClicked = () => {
            return _promiseFactory.defer((deferredObject) => {
                _facade.resetNumberTypeSelection()
                    .fail(deferredObject.reject)
                    .done(() => {
                        _facade.setPhoneNumberCompleted(self.signupSessionHasNumber())
                            .fail(deferredObject.reject)
                            .done(() => {
                                deferredObject.resolve();
                                _signupNavigationManager.previousPage();
                            });
                    });
            });
        };

        self.onForwardArrowClicked = () => {
            return _promiseFactory.defer((deferredObject) => {
                if (!self.signupSessionHasNumber()) {
                    self.showValidationMessage(true);
                    deferredObject.resolve(false);
                } else {
                    _facade.setPhoneNumberCompleted(self.signupSessionHasNumber())
                        .fail(deferredObject.reject)
                        .done(() => {
                            deferredObject.resolve(true);
                        });
                }
            });
        };

        const _showNumberReservationModal = (phoneNumber) => {
            self.modalPhoneNumber(_phoneNumberFormatter.toUSAreaCode(phoneNumber));
            self.isModalActive(true);
            setTimeout(() => {
                self.isModalVisible(true);
            }, 100);
        };

        self.modalCloseClick = () => {
            self.isModalVisible(false);
            setTimeout(() => {
                self.isModalActive(false);
            }, 250);
        };

        self.isTabActive = (testTabEnumeration) => {
            return testTabEnumeration === self.activeTab();
        };

        self.detached = function() {
            _signupNavigationManager.detached();
            _disposables.forEach(d => d.dispose());
        };

        self.compositionComplete = () => {
            self.isCompositionComplete(true);
        };

        self.activate = () => {
            const FacadeConstructor = require('presentation/signup/facades/signupPhoneNumberLocalFacade');
            _facade = new FacadeConstructor();
            _facade.init();

            const SignupNavigationManagerConstructor = require('presentation/signup/common/signupNavigationManager');
            _signupNavigationManager = new SignupNavigationManagerConstructor();
            _signupNavigationManager.init();

            self.flow(_signupNavigationManager.getFlowId());

            return _initialize();
        };

        const _initialize = () => {
            _promiseFactory.deferIndefinitely(promise => {
                _facade.get()
                    .done((signupSession) => {
                        const phoneNumber = signupSession.reservedPhoneNumber;
                        const vanityString = signupSession.reservedPhoneNumberVanityString || '';
                        self.vanityString(vanityString);
                        self.searchType(_numberPickerConstants.searchTypeLookup[signupSession.reservedPhoneNumberSearchType] || _numberPickerConstants.searchTypeConstants.default);

                        if (signupSession.signupSessionStatus === _signupSessionStatusConstants.numberProvisioningFailed) {
                            _facade.clearSessionStateReservedPhoneNumber();
                            promise.resolve();
                        } else if (phoneNumber === null || phoneNumber === undefined || _phoneNumberFormatter.isTollFree(phoneNumber)) {
                            promise.resolve();
                        } else {
                            const city = signupSession.reservedPhoneNumberCity;
                            const region = signupSession.reservedPhoneNumberRegion;
                            const npa = _phoneNumberFormatter.getNpa(phoneNumber);
                            self.geoLocation = {
                                countryCode: region.substring(0, 2),
                                regionCode: region,
                                city,
                                npa,
                            };
                            _facade.reReserveSessionPhoneNumber()
                                .fail(promise.reject)
                                .done((response) => {
                                    if (response.status !== _numberPickerConstants.successfullyReserved) {
                                        _showNumberReservationModal(phoneNumber);
                                    } else {
                                        const numberPresentationObject = new NumberPresentationObjectConstructor({
                                            phoneNumber,
                                            region,
                                            city,
                                            vanityString
                                        });
                                        numberPresentationObject.isSelected(true);
                                        numberPresentationObject.isChecked(true);
                                        self.selectedPhoneNumbers([numberPresentationObject]);
                                    }
                                    promise.resolve();
                                });
                        }
                    });
            });

            return _promiseFactory.wait();
        };
    };
});
